import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/industries`; // Change to your backend URL
const PRODUCTS_API = "http://localhost:5000/api/products";

const IndustryDetailPage = () => {
  const { slug } = useParams();
  const [industry, setIndustry] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchIndustry();
    fetchProducts();
  }, [slug]);

  const fetchIndustry = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/${slug}`);
      if (!response.ok) throw new Error("Industry not found");
      const data = await response.json();
      setIndustry(data.data || data);
    } catch (err) {
      setError(err.message);
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${PRODUCTS_API}/getAll`);
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      setProducts(data.data || data);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const getProductName = (productId) => {
    const product = products.find((p) => p._id === productId);
    return product?.heading || "Unknown Product";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading industry details...</p>
        </div>
      </div>
    );
  }

  if (error || !industry) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-8 py-16">
          <Link
            to="/industries"
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8"
          >
            <ArrowLeft size={20} />
            Back to Industries
          </Link>

          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg">
            {error || "Industry not found"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="max-w-6xl mx-auto px-8 py-8">
        <Link
          to="/industries"
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-8 transition"
        >
          <ArrowLeft size={20} />
          Back to All Industries
        </Link>
      </div>

      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-16">
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex items-start gap-6">
            <div className="text-7xl bg-white bg-opacity-20 p-6 rounded-xl">
              {industry.icon}
            </div>
            <div className="flex-1">
              <h1 className="text-5xl font-bold mb-4">{industry.title}</h1>
              <p className="text-xl text-blue-100 leading-relaxed max-w-2xl">
                {industry.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Benefits */}
          <div className="lg:col-span-2">
            {/* Key Metrics */}
            {industry.stats && (
              <div className="mb-12 p-8 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200">
                <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">
                  Key Metric
                </h2>
                <div className="flex items-baseline gap-2">
                  <div className="text-5xl font-bold text-blue-600">
                    {industry.stats.metric}
                  </div>
                  <div className="text-lg text-gray-700">
                    {industry.stats.label}
                  </div>
                </div>
              </div>
            )}

            {/* Benefits Section */}
            {industry.benefits && industry.benefits.length > 0 && (
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Key Benefits
                </h2>
                <div className="space-y-4">
                  {industry.benefits.map((benefit, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-4 p-6 bg-white rounded-lg border border-gray-200 hover:border-yellow-400 hover:shadow-md transition"
                    >
                      <div className="text-3xl text-yellow-500 flex-shrink-0">
                        ✓
                      </div>
                      <div>
                        <p className="text-gray-700 font-medium text-lg">
                          {benefit}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Core Products */}
          <div>
            <div className="sticky top-24 bg-white rounded-xl border-4 border-yellow-400 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Core Products
              </h3>

              {industry.solutions && industry.solutions.length > 0 ? (
                <div className="space-y-3">
                  {industry.solutions.map((solution, idx) => {
                    const productName = getProductName(solution.productId);
                    const product = products.find(
                      (p) => p._id === solution.productId
                    );

                    return (
                      <div
                        key={idx}
                        className="p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 hover:shadow-md transition cursor-pointer"
                      >
                        <div className="font-semibold text-gray-900 text-sm">
                          {productName}
                        </div>
                        {product?.tagline && (
                          <div className="text-xs text-gray-600 mt-1">
                            {product.tagline}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No products assigned yet</p>
                </div>
              )}

              <Link
                to="/products"
                className="block mt-8 w-full text-center px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition"
              >
                View All Products
              </Link>
            </div>
          </div>
        </div>

        {/* Related Industries */}
        <div className="mt-20 pt-12 border-t border-gray-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Other Industries
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Would show related industries here */}
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200 col-span-full">
              <p className="text-gray-600">
                Explore other industry solutions in the main industries page
              </p>
              <Link
                to="/industries"
                className="inline-block mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                View All Industries →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-16 mt-20">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Transform Your {industry.title} Operations?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Contact our team to learn how our solutions can benefit your
            organization.
          </p>
          <button className="px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition text-lg">
            Get in Touch
          </button>
        </div>
      </div>
    </div>
  );
};

export default IndustryDetailPage;